import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Card,
  Chip,
  Grid,
  Alert,
  Stack,
  Button,
  Divider,
  IconButton,
  Typography,
  CardContent,
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';
import {
  ActivationTerms,
  ActivationOrigin,
  ActivationRequestStatus,
} from 'src/services/admin/admin.types';
import {
  useDeclineTenantMutation,
  useGetActivationRequestQuery,
  useSendActivationLinkMutation,
  useActivateTenantWithoutPaymentMutation,
} from 'src/services/admin/admin.service';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

import { TenantType } from 'src/types/enums';
import { PayPeriod } from 'src/types/payment';

import ActivationTermsFields from './activation-terms-fields';

type Props = {
  organizationId: string;
  tenantType?: TenantType;
};

const STATUS_COLOUR: Record<ActivationRequestStatus, 'warning' | 'info' | 'success' | 'error'> = {
  [ActivationRequestStatus.PENDING_REVIEW]: 'warning',
  [ActivationRequestStatus.LINK_SENT]: 'info',
  [ActivationRequestStatus.COMPLETED]: 'success',
  [ActivationRequestStatus.DECLINED]: 'error',
};

/**
 * Review and activation controls for a gated agency/employer tenant.
 *
 * Shows what the applicant asked for - including their free text seat answer verbatim - and lets a
 * super admin set the real commercial terms and either email a payment link or activate the tenant
 * outright for complimentary and internal accounts.
 */
export default function ActivationCard({ organizationId, tenantType }: Props) {
  const { t } = useTranslate();

  const confirmActivate = useBoolean();
  const confirmDecline = useBoolean();

  const [terms, setTerms] = useState<ActivationTerms>({
    product_identifier: '',
    billing_period: PayPeriod.Monthly,
    seats: 1,
    promo_code: '',
  });

  const { data: request, isLoading, isError } = useGetActivationRequestQuery(
    { id: organizationId },
    { skip: !organizationId }
  );

  const [sendLink, { isLoading: isSending }] = useSendActivationLinkMutation();
  const [activateWithoutPayment, { isLoading: isActivating }] = useActivateTenantWithoutPaymentMutation();
  const [declineTenant, { isLoading: isDeclining }] = useDeclineTenantMutation();

  useEffect(() => {
    if (!request) return;

    setTerms({
      product_identifier: request.product_identifier ?? '',
      billing_period: request.billing_period ?? PayPeriod.Monthly,
      seats: request.seats ?? 1,
      promo_code: request.promo_code ?? '',
    });
  }, [request]);

  // tenants that were never gated simply have no request
  if (isLoading || isError || !request) {
    return null;
  }

  const isCompleted = request.status === ActivationRequestStatus.COMPLETED;

  const handleSendLink = async () => {
    try {
      await sendLink({
        id: organizationId,
        data: { ...terms, promo_code: terms.promo_code || undefined },
      }).unwrap();

      enqueueSnackbar(t('restricted.organizations.detail.activation.api.link_sent'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ?? t('restricted.organizations.detail.activation.api.default_error'),
        { variant: 'error' }
      );
    }
  };

  const handleActivate = async () => {
    confirmActivate.onFalse();

    try {
      await activateWithoutPayment({
        id: organizationId,
        data: {
          product_identifier: terms.product_identifier || undefined,
          billing_period: terms.billing_period,
          seats: terms.seats,
        },
      }).unwrap();

      enqueueSnackbar(t('restricted.organizations.detail.activation.api.activated'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ?? t('restricted.organizations.detail.activation.api.default_error'),
        { variant: 'error' }
      );
    }
  };

  const handleDecline = async () => {
    confirmDecline.onFalse();

    try {
      await declineTenant({ id: organizationId }).unwrap();
      enqueueSnackbar(t('restricted.organizations.detail.activation.api.declined'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ?? t('restricted.organizations.detail.activation.api.default_error'),
        { variant: 'error' }
      );
    }
  };

  const copyLink = () => {
    if (!request.activation_url) return;

    navigator.clipboard.writeText(request.activation_url);
    enqueueSnackbar(t('common.copied'), { variant: 'success' });
  };

  const renderDetail = (label: string, value?: string | null) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Grid>
  );

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2}>

          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                {t('restricted.organizations.detail.activation.title')}
              </Typography>

              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  color={STATUS_COLOUR[request.status]}
                  label={t(`restricted.organizations.detail.activation.status.${request.status}`)}
                />
                {request.origin === ActivationOrigin.ADMIN_CREATED && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('restricted.organizations.detail.activation.admin_created')}
                  />
                )}
              </Stack>
            </Stack>
          </Grid>

          {/* what the applicant told us */}
          {renderDetail(t('restricted.organizations.detail.activation.company'), request.company_name)}
          {renderDetail(t('restricted.organizations.detail.activation.tax_id'), request.tax_id)}
          {renderDetail(t('restricted.organizations.detail.activation.contact'), request.contact_name)}
          {renderDetail(t('restricted.organizations.detail.activation.contact_email'), request.contact_email)}
          {renderDetail(t('restricted.organizations.detail.activation.contact_phone'), request.contact_phone)}
          {renderDetail(
            t('restricted.organizations.detail.activation.requested_seats'),
            request.requested_seats
          )}

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {isCompleted ? (
            <Grid item xs={12}>
              <Alert severity="success">
                {t('restricted.organizations.detail.activation.completed_message', {
                  date: request.completed_at ? fDateTime(request.completed_at) : '',
                })}
              </Alert>
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  {t('restricted.organizations.detail.activation.terms_title')}
                </Typography>
              </Grid>

              <ActivationTermsFields
                tenantType={tenantType}
                value={terms}
                onChange={setTerms}
              />

              {request.status === ActivationRequestStatus.LINK_SENT && request.activation_url && (
                <Grid item xs={12}>
                  <Alert
                    severity="info"
                    action={
                      <IconButton size="small" onClick={copyLink} title={t('common.copy')}>
                        <Iconify icon="solar:copy-bold" />
                      </IconButton>
                    }
                  >
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        {t('restricted.organizations.detail.activation.link_sent_message', {
                          date: request.approved_at ? fDateTime(request.approved_at) : '',
                          expires: request.expires_at ? fDateTime(request.expires_at) : '',
                        })}
                      </Typography>
                      {request.first_opened_at && (
                        <Typography variant="caption" color="text.secondary">
                          {t('restricted.organizations.detail.activation.first_opened', {
                            date: fDateTime(request.first_opened_at),
                          })}
                        </Typography>
                      )}
                    </Stack>
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
                  <Button
                    color="error"
                    variant="text"
                    onClick={confirmDecline.onTrue}
                    disabled={isDeclining}
                  >
                    {t('restricted.organizations.detail.activation.decline')}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={confirmActivate.onTrue}
                    disabled={isActivating}
                  >
                    {t('restricted.organizations.detail.activation.activate_without_payment')}
                  </Button>

                  <LoadingButton
                    variant="contained"
                    color="success"
                    onClick={handleSendLink}
                    loading={isSending}
                    disabled={!terms.product_identifier}
                    data-testid="send-activation-link-btn"
                  >
                    {request.status === ActivationRequestStatus.LINK_SENT
                      ? t('restricted.organizations.detail.activation.resend_link')
                      : t('restricted.organizations.detail.activation.send_link')}
                  </LoadingButton>
                </Stack>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>

      <ConfirmDialog
        open={confirmActivate.value}
        onClose={confirmActivate.onFalse}
        title={t('restricted.organizations.detail.activation.activate_without_payment')}
        content={t('restricted.organizations.detail.activation.activate_confirm')}
        action={
          <LoadingButton variant="contained" onClick={handleActivate} loading={isActivating}>
            {t('common.confirm')}
          </LoadingButton>
        }
      />

      <ConfirmDialog
        open={confirmDecline.value}
        onClose={confirmDecline.onFalse}
        title={t('restricted.organizations.detail.activation.decline')}
        content={t('restricted.organizations.detail.activation.decline_confirm')}
        action={
          <LoadingButton variant="contained" color="error" onClick={handleDecline} loading={isDeclining}>
            {t('common.confirm')}
          </LoadingButton>
        }
      />
    </Card>
  );
}
